import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  ChevronLeft,
  ChevronRight,
  ArrowLeft,
  ImageIcon,
  Target,
  CheckCircle2,
  Calendar,
  FolderOpen,
  Eye,
  Download,
  Zap
} from "lucide-react";
import { message, Spin, Modal, Button, InputNumber, Select } from "antd";
import axios from "../../utils/axios";

const { Option } = Select;

const Viewdatasetversion = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [projectsLoading, setProjectsLoading] = useState(true);
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [currentOutputFileIndex, setCurrentOutputFileIndex] = useState(0);
  const [outputFiles, setOutputFiles] = useState([]);
  const [exportModalVisible, setExportModalVisible] = useState(false);
  const [trainModalVisible, setTrainModalVisible] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [progress, setProgress] = useState(0);
  const [exportType, setExportType] = useState(null);
  const user = JSON.parse(localStorage.getItem("user"));
  const projectname =
    location.state?.projectname ||
    localStorage.getItem("projectname") || 
    "";
  const organizationId = localStorage.getItem("organizationId");
  const canvasRef = useRef(null);
  const imageRef = useRef(null);
  const [cardLoading, setCardLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [pageLoading, setPageLoading] = useState(false);
  const limit = 6;

  const [trainConfig, setTrainConfig] = useState({
    model: "yolov8n",
    epochs: 50,
    batchSize: 16
  });

  const fetchDatasetVersion = async (pageNumber = 1, project = null) => {
    try {
      if (pageNumber !== page) {
        setPageLoading(true);
      } else {
        setLoading(true);
      }

      const payload = {
        projectName: project ? project.projectName : projectname,
        username: user?.username,
        page: pageNumber,
        limit: limit
      };

      const response = await axios.post("/fetch-datasetversion", payload);

      if (response.status === 200) {
        const data = response.data;

        if (!data?.versions?.length) {
          message.error("No dataset versions found");
          setProjects([]);
          return;
        }

        // ✅ Only map dataset info + imageCount (NO images)
        const formattedProjects = data.versions.map((version) => ({
          projectName: version.projectName,
          versionId: version.versionId,
          versionName: version.versionName,
          createdBy: version.createdBy,
          createdOn: version.createdOn,
          imageCount: version.imageCount || 0
        }));

        setProjects(formattedProjects);

        setTotalPages(data.totalPages);
        setPage(data.currentPage);

      } else {
        message.error("Unexpected response from server");
      }

    } catch (error) {

      if (error.response?.status === 404) {
        setProjects([]);
      } else {
        message.error(
          error.response?.data?.message || "Failed to fetch dataset version"
        );
      }
    } finally {
      setLoading(false);
      setPageLoading(false);
      setProjectsLoading(false);
    }
  };

  useEffect(() => {
    fetchDatasetVersion(1);

    if (location.state?.selectedProject) {
      fetchDatasetVersion(1, location.state.selectedProject);
    }
  }, [location.state]);

  const handleExport = async (type) => {
    try {
      setExportType(type);
      setExporting(true);
      setProgress(0);

      const payload = {
        projectName: selectedProject.projectName,
        versionName: selectedProject.versionName,
        exportType: type
      };

      const startRes = await axios.post('/export-dataset', payload);
      const { jobId } = startRes.data;

      const interval = setInterval(async () => {
        const statusRes = await axios.get(`/export-dataset/status/${jobId}`);
        const { status, progress } = statusRes.data;

        setProgress(progress);

        if (status === 'completed' && progress === 100) {
          clearInterval(interval);

          const downloadRes = await axios.get(
            `/export-dataset/download/${jobId}`,
            { responseType: 'blob' }
          );

          const url = window.URL.createObjectURL(
            new Blob([downloadRes.data])
          );

          const link = document.createElement('a');
          link.href = url;
          link.setAttribute(
            'download',
            `${selectedProject.projectName}_${type}.zip`
          );

          document.body.appendChild(link);
          link.click();
          link.remove();
          window.URL.revokeObjectURL(url);

          message.success("Dataset exported successfully!");

          setTimeout(() => {
            setExporting(false);
            setExportModalVisible(false);
            setProgress(0);
          }, 800);
        }

        if (status === 'failed') {
          clearInterval(interval);
          message.error("Export failed");
          setExporting(false);
        }
      }, 1000);

    } catch (err) {
      message.error("Failed to start export");
      setExporting(false);
    }
  };

  const handleTrain = async () => {
    try {
      setTrainModalVisible(false);
      setLoading(true);
      const payload = {
        projectName: selectedProject.projectName,
        versionName: selectedProject.versionName,
        model: trainConfig.model,
        epochs: trainConfig.epochs,
        batchSize: trainConfig.batchSize
      };
      const response = await axios.post('/train-model', payload);
      if (response.status === 200) {
        message.success('Training started successfully!');
      }
    } catch (err) {
      if (err.response?.data?.msg) {
        message.warning(err.response.data.msg);
      } else if (err.response?.status === 401) {
        message.error("User not authorised");
      } else {
        message.error("Something went wrong");
      }
    } finally {
      setLoading(false);
    }
  };
  
  // const handleProjectClick = (project) => {
  //   setSelectedProject(project)
  //   setOutputFiles(project.outputFiles || project.images || []);
  //   setCurrentOutputFileIndex(0);
  // };

  const handleProjectClick = async (project) => {
    try {
      setCardLoading(true);

      const res = await axios.post("/fetch-datasetversion", {
        versionId: project.versionId,
        loadImages: true
      });

      setSelectedProject(project);
      setOutputFiles(res.data.images || []);
      setCurrentOutputFileIndex(0);

    } catch (err) {
      message.error("Failed to load images");
    } finally {
      setCardLoading(false);
    }
  };

  const handleClose = () => {
    if (!exporting) {
      setExportModalVisible(false);
    }
  };

  // FIXED: drawAnnotations now supports rectangle, circle, polygon, polyline
  const drawAnnotations = useCallback(() => {
    const canvas = canvasRef.current;
    const image = imageRef.current;
    if (!canvas || !image || !outputFiles.length) return;

    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    const currentOutputFile = outputFiles[currentOutputFileIndex];
    const annotations = currentOutputFile.annotations || [];

    annotations.forEach((annotation, index) => {
      ctx.strokeStyle = '#3b82f6';
      ctx.lineWidth = 2;
      ctx.setLineDash([]);
      ctx.fillStyle = '#3b82f6';
      ctx.font = '12px Inter, system-ui, sans-serif';

      const label = annotation.class || `Region ${index + 1}`;
      const textMetrics = ctx.measureText(label);
      const padding = 6;
      const labelWidth = textMetrics.width + padding * 2;
      const labelHeight = 20;

      let labelX = 0, labelY = 0;

      switch (annotation.type) {
        case 'rectangle':
          ctx.strokeRect(annotation.x, annotation.y, annotation.width, annotation.height);
          labelX = annotation.x;
          labelY = annotation.y;
          break;

        case 'circle':
          if (annotation.radius) {
            ctx.beginPath();
            ctx.arc(annotation.x, annotation.y, annotation.radius, 0, 2 * Math.PI);
            ctx.stroke();
            labelX = annotation.x - annotation.radius;
            labelY = annotation.y - annotation.radius;
          }
          break;

        case 'polygon':
        case 'polyline':
          const points = annotation.points || [];
          if (points.length > 1) {
            ctx.beginPath();
            ctx.moveTo(points[0].x, points[0].y);
            for (let i = 1; i < points.length; i++) {
              ctx.lineTo(points[i].x, points[i].y);
            }
            if (annotation.type === 'polyline') {
              ctx.lineTo(points[0].x, points[0].y); // Close polyline
            } else {
              ctx.closePath(); // Close polygon
            }
            ctx.stroke();
            labelX = points[0].x;
            labelY = points[0].y;
          }
          break;

        default:
          return;
      }

      // Draw label background and text
      ctx.fillStyle = '#3b82f6';
      ctx.fillRect(labelX, labelY - labelHeight, labelWidth, labelHeight);
      ctx.fillStyle = 'white';
      ctx.textBaseline = 'middle';
      ctx.fillText(label, labelX + padding, labelY - labelHeight / 2);
    });
  }, [outputFiles, currentOutputFileIndex]);

  const handleImageLoad = () => {
    const canvas = canvasRef.current;
    const image = imageRef.current;
    if (!canvas || !image) return;

    canvas.width = image.naturalWidth;
    canvas.height = image.naturalHeight;
    canvas.style.width = `${image.clientWidth}px`;
    canvas.style.height = `${image.clientHeight}px`;

    drawAnnotations();
  };

  useEffect(() => {
    if (selectedProject && outputFiles.length > 0) {
      drawAnnotations();
    }
  }, [drawAnnotations, selectedProject, currentOutputFileIndex]);

  const goToPrevious = () => {
    setCurrentOutputFileIndex((prev) =>
      prev > 0 ? prev - 1 : outputFiles.length - 1
    );
  };

  const goToNext = () => {
    setCurrentOutputFileIndex((prev) =>
      prev < outputFiles.length - 1 ? prev + 1 : 0
    );
  };

  const getCurrentAnnotations = () => {
    if (!outputFiles.length) return [];
    return outputFiles[currentOutputFileIndex]?.annotations || [];
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleExportClick = () => {
    setExportModalVisible(true);
  };

  const handleTrainModalClose = () => {
    setTrainModalVisible(false);
  };

  if (!selectedProject) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-50">
        <div className="max-w-7xl mx-auto px-6 py-8">
          {projectsLoading ? (
            <div className="flex items-center justify-center py-20">
              <Spin size="large" tip="Loading datasets..." />
            </div>
          ) : projects.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20">
              <div className="text-center p-8">
                <FolderOpen className="w-16 h-16 text-slate-400 mx-auto mb-4" />
                <h2 className="text-xl font-semibold text-slate-600 mb-2">
                  No Datasets Found
                </h2>
                <p className="text-slate-500 mb-4">
                  You haven't created any datasets yet
                </p>
              </div>
            </div>
          ) : (
            <div>
              <div className="mb-8">
                <h1 className="text-xl font-bold text-slate-800 flex items-center">
                  <Eye className="w-5 h-5 mr-2 text-blue-600" />
                  View Output Files
                </h1>
                <p className="text-sm text-slate-500 mt-0.5">Browse your saved output files</p>
              </div>
              <Spin spinning={pageLoading} tip="Loading next page...">
                <Spin spinning={cardLoading} tip="Loading dataset images...">
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {projects.map((project, index) => (
                      <div
                        key={index}
                        onClick={() => handleProjectClick(project)}
                        className="group bg-white rounded-2xl shadow-lg border border-slate-200/60 hover:shadow-xl hover:border-slate-300/60 transition-all duration-300 cursor-pointer overflow-hidden"
                      >
                        <div className="p-6">
                          <div className="flex items-start justify-between mb-4">
                            <div className="flex items-center space-x-3">
                              <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center shadow-lg">
                                <Target className="w-6 h-6 text-white" />
                              </div>
                              <div>
                                <h3 className="font-semibold text-slate-800 group-hover:text-blue-600 transition-colors">
                                  {project.projectName}
                                </h3>
                                <p className="text-sm text-slate-500">Version: {project.versionName}</p>
                                <p className="text-sm text-slate-500">Created by: {project.createdBy}</p>
                              </div>
                            </div>
                            <div className="flex items-center space-x-2">
                              <div className="opacity-0 group-hover:opacity-100 transition-opacity">
                                <ChevronRight className="w-5 h-5 text-slate-400" />
                              </div>
                            </div>
                          </div>
                          <div className="space-y-3">
                            <div className="flex items-center justify-between p-3 bg-gradient-to-r from-slate-50 to-slate-50/50 rounded-lg">
                              <div className="flex items-center space-x-2 text-sm text-slate-600">
                                <ImageIcon className="w-4 h-4" />
                                <span>Output Files</span>
                              </div>
                              <span className="text-sm font-medium text-emerald-600">
                                {project.imageCount}
                              </span>
                            </div>
                            {project.createdOn && (
                              <div className="flex items-center space-x-2 text-xs text-slate-500 pt-2 border-t border-slate-100">
                                <Calendar className="w-3 h-3" />
                                <span>Created on {formatDate(project.createdOn)}</span>
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="h-1 bg-gradient-to-r from-blue-500 via-purple-500 to-emerald-500 opacity-0 group-hover:opacity-100 transition-opacity"></div>
                      </div>
                    ))}
                  </div>
                </Spin>
              </Spin>

              {/* Pagination */}
              <div className="flex justify-center items-center mt-8 gap-3">

                <button
                  disabled={page === 1}
                  onClick={() => fetchDatasetVersion(page - 1)}
                  className="px-4 py-2 border rounded-lg disabled:opacity-50"
                >
                  <ChevronLeft size={18} />
                </button>

                <span className="text-sm font-medium">
                  Page {page} of {totalPages}
                </span>

                <button
                  disabled={page === totalPages}
                  onClick={() => fetchDatasetVersion(page + 1)}
                  className="px-4 py-2 border rounded-lg disabled:opacity-50"
                >
                  <ChevronRight size={18} />
                </button>

              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  const currentAnnotations = getCurrentAnnotations();

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-50">
      <div className="bg-white/80 backdrop-blur-sm border-b border-slate-200/60 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 py-5">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => setSelectedProject(null)}
                className="flex items-center px-3 py-2 text-slate-600 hover:text-slate-800 hover:bg-slate-100 rounded-lg transition-all duration-200 group"
              >
                <ArrowLeft className="w-4 h-4 mr-2 group-hover:-translate-x-0.5 transition-transform" />
                <span className="font-medium">Back to Datasets</span>
              </button>
              <div className="h-6 w-px bg-slate-300"></div>
              <div>
                <h1 className="text-xl font-bold text-slate-800 flex items-center">
                  <Eye className="w-5 h-5 mr-2 text-blue-600" />
                  {selectedProject.projectName} (Version: {selectedProject.versionName})
                </h1>
                <p className="text-sm text-slate-500 mt-0.5">
                  Created by: {selectedProject.createdBy} • Viewing output files
                </p>
              </div>
            </div>

            <div className="flex items-center space-x-3">
              {/* <button
                onClick={handleExportClick}
                className="flex items-center px-4 py-2 text-slate-600 hover:text-slate-800 hover:bg-slate-100 rounded-lg transition-all duration-200 border border-slate-200 hover:border-slate-300 hover:shadow-sm"
              >
                <Download className="w-4 h-4 mr-2" />
                <span className="font-medium">Export</span>
              </button> */}

              <div className="hidden sm:flex items-center space-x-2 text-sm">
                <div className="flex items-center space-x-1 text-slate-600">
                  <CheckCircle2 className="w-3 h-3" />
                  <span>{currentOutputFileIndex + 1} of {outputFiles.length}</span>
                </div>
                <div className="w-px h-4 bg-slate-300"></div>
                <div className="flex items-center space-x-1 text-emerald-600">
                  <Target className="w-3 h-3" />
                  <span>{currentAnnotations.length} annotations</span>
                </div>
              </div>

              <div className="w-32 h-2 bg-slate-200 rounded-full overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-blue-500 to-emerald-500 rounded-full transition-all duration-300"
                  style={{ width: `${((currentOutputFileIndex + 1) / outputFiles.length) * 100}%` }}
                ></div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {loading && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/20 z-50">
          <Spin size="large" tip="Processing..." />
        </div>
      )}

      {/* Export Modal */}
     <Modal
      title={
        <div className="flex items-center gap-2">
          <Download className="w-5 h-5 text-blue-500" />
          <span className="text-lg font-semibold">
            {exporting ? "Exporting Dataset..." : "Export Dataset"}
          </span>
        </div>
      }
      open={exportModalVisible}   // ✅ FIXED
      onCancel={handleClose}
      footer={null}
      width={480}
      centered
      closable={!exporting}
      maskClosable={!exporting}
    >

        {exporting ? (
          <div className="py-8">
            <div className="flex flex-col items-center justify-center space-y-6">
              <div className="relative">
                <svg className="w-32 h-32 transform -rotate-90">
                  <circle
                    cx="64"
                    cy="64"
                    r="56"
                    stroke="#e5e7eb"
                    strokeWidth="8"
                    fill="none"
                  />
                  <circle
                    cx="64"
                    cy="64"
                    r="56"
                    stroke="url(#gradient)"
                    strokeWidth="8"
                    fill="none"
                    strokeDasharray={`${2 * Math.PI * 56}`}
                    strokeDashoffset={`${2 * Math.PI * 56 * (1 - progress / 100)}`}
                    strokeLinecap="round"
                    className="transition-all duration-300 ease-out"
                  />
                  <defs>
                    <linearGradient id="gradient" x1="0%" y1="0%" x2="100%" y2="100%">
                      <stop offset="0%" stopColor="#3b82f6" />
                      <stop offset="100%" stopColor="#10b981" />
                    </linearGradient>
                  </defs>
                </svg>
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="text-center">
                    <div className="text-3xl font-bold text-gray-800">
                      {Math.round(progress)}%
                    </div>
                    <div className="text-xs text-gray-500 mt-1">
                      {progress < 30 && "Preparing..."}
                      {progress >= 30 && progress < 70 && "Processing..."}
                      {progress >= 70 && progress < 100 && "Finalizing..."}
                      {progress === 100 && "Complete!"}
                    </div>
                  </div>
                </div>
              </div>

              <div className="w-full max-w-xs">
                <div className="flex items-center justify-between text-sm mb-2">
                  <span className="text-gray-600 font-medium">
                    Exporting {exportType === 'yolov8' ? 'Standard' : 'OBB'} Format
                  </span>
                </div>
                <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-blue-500 to-emerald-500 rounded-full transition-all duration-300 ease-out"
                    style={{ width: `${progress}%` }}
                  />
                </div>
              </div>

              {progress === 100 && (
                <div className="flex items-center gap-2 text-emerald-600 animate-bounce">
                  <CheckCircle2 className="w-5 h-5" />
                  <span className="font-medium">Download starting...</span>
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="flex flex-col space-y-3 pt-4">
            <button
              onClick={() => handleExport('yolov8')}
              className="group relative overflow-hidden rounded-lg border-2 border-blue-200 bg-gradient-to-r from-blue-50 to-blue-100 p-4 text-left transition-all duration-200 hover:border-blue-400 hover:shadow-md hover:scale-[1.02] active:scale-[0.98]"
            >
              <div className="flex items-start gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-500 text-white shadow-sm">
                  <Target className="w-5 h-5" />
                </div>
                <div className="flex-1">
                  <h3 className="text-base font-semibold text-gray-800">
                    YOLOv8 Standard Format
                  </h3>
                  <p className="mt-1 text-sm text-gray-600">
                    Standard bounding boxes for object detection tasks
                  </p>
                </div>
                <svg className="w-5 h-5 text-gray-400 transition-transform group-hover:translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </div>
            </button>

            <button
              onClick={() => handleExport('yolov8-obb')}
              className="group relative overflow-hidden rounded-lg border-2 border-purple-200 bg-gradient-to-r from-purple-50 to-purple-100 p-4 text-left transition-all duration-200 hover:border-purple-400 hover:shadow-md hover:scale-[1.02] active:scale-[0.98]"
            >
              <div className="flex items-start gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-purple-500 text-white shadow-sm">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7l4-4m0 0l4 4m-4-4v18m-8-6l4 4m0 0l4-4" />
                  </svg>
                </div>
                <div className="flex-1">
                  <h3 className="text-base font-semibold text-gray-800">
                    YOLOv8 Oriented Bounding Boxes
                  </h3>
                  <p className="mt-1 text-sm text-gray-600">
                    Rotated boxes for detecting objects at any angle
                  </p>
                </div>
                <svg className="w-5 h-5 text-gray-400 transition-transform group-hover:translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </div>
            </button>

            <div className="mt-2 flex items-start gap-2 rounded-lg bg-gray-50 p-3 border border-gray-200">
              <svg className="w-5 h-5 text-gray-500 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
              <p className="text-xs text-gray-600">
                Your dataset will be downloaded in the selected format, ready for training.
              </p>
            </div>
          </div>
        )}
      </Modal>

      {/* Train Model Modal */}
      <Modal
        title={
          <div className="flex items-center gap-2">
            <Zap className="w-5 h-5 text-emerald-500" />
            <span className="text-lg font-semibold">Train Model</span>
          </div>
        }
        open={trainModalVisible}
        onCancel={handleTrainModalClose}
        footer={null}
        width={520}
        centered
      >
        <div className="space-y-5 pt-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Model Architecture</label>
            <Select
              value={trainConfig.model}
              onChange={(value) => setTrainConfig({ ...trainConfig, model: value })}
              className="w-full"
              size="large"
            >
              <Option value="yolov8n">YOLOv8n (Nano)</Option>
              <Option value="yolov8s">YOLOv8s (Small)</Option>
              <Option value="yolov8m">YOLOv8m (Medium)</Option>
              <Option value="yolov8l">YOLOv8l (Large)</Option>
              <Option value="yolov8x">YOLOv8x (XLarge)</Option>
            </Select>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Epochs</label>
              <InputNumber
                min={1}
                max={500}
                value={trainConfig.epochs}
                onChange={(value) => setTrainConfig({ ...trainConfig, epochs: value })}
                className="w-full"
                size="large"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Batch Size</label>
              <InputNumber
                min={1}
                max={128}
                value={trainConfig.batchSize}
                onChange={(value) => setTrainConfig({ ...trainConfig, batchSize: value })}
                className="w-full"
                size="large"
              />
            </div>
          </div>

          <div className="flex items-start gap-2 rounded-lg bg-amber-50 p-3 border border-amber-200">
            <svg className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
            <p className="text-xs text-amber-700">
              Training will use your annotated dataset. Larger models require more GPU memory.
            </p>
          </div>

          <div className="flex justify-end space-x-3 pt-2">
            <Button onClick={handleTrainModalClose} size="large">
              Cancel
            </Button>
            <Button
              type="primary"
              size="large"
              onClick={handleTrain}
              className="bg-gradient-to-r from-emerald-500 to-teal-600 border-none"
            >
              <Zap className="w-4 h-4 inline mr-1" />
              Start Training
            </Button>
          </div>
        </div>
      </Modal>

      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="grid grid-cols-1 xl:grid-cols-4 gap-8">
          <div className="xl:col-span-3 space-y-6">
            <div className="bg-white rounded-2xl shadow-xl shadow-slate-200/50 overflow-hidden border border-slate-200/60">
              <div className="flex items-center justify-between p-6 bg-gradient-to-r from-slate-50 to-white border-b border-slate-200/60">
                <button
                  onClick={goToPrevious}
                  disabled={outputFiles.length <= 1}
                  className="flex items-center px-4 py-2.5 text-slate-600 hover:text-slate-800 hover:bg-white disabled:opacity-50 disabled:cursor-not-allowed rounded-xl transition-all duration-200 border border-slate-200 hover:border-slate-300 hover:shadow-sm"
                >
                  <ChevronLeft className="w-4 h-4 mr-2" />
                  <span className="font-medium">Previous</span>
                </button>

                <div className="text-center">
                  <div className="text-sm font-semibold text-slate-700">
                    Output File {currentOutputFileIndex + 1} of {outputFiles.length}
                  </div>
                  <div className="text-xs text-slate-500 mt-1">
                    {currentAnnotations.length} annotation{currentAnnotations.length !== 1 ? 's' : ''}
                  </div>
                </div>

                <button
                  onClick={goToNext}
                  disabled={outputFiles.length <= 1}
                  className="flex items-center px-4 py-2.5 text-slate-600 hover:text-slate-800 hover:bg-white disabled:opacity-50 disabled:cursor-not-allowed rounded-xl transition-all duration-200 border border-slate-200 hover:border-slate-300 hover:shadow-sm"
                >
                  <span className="font-medium">Next</span>
                  <ChevronRight className="w-4 h-4 ml-2" />
                </button>
              </div>
              <div className="p-8 bg-gradient-to-br from-slate-50/50 to-white flex items-center justify-center">
                {outputFiles.length > 0 ? (
                  <div className="relative rounded-xl overflow-hidden shadow-lg border border-slate-200/60 max-w-full">
                    <img
                      ref={imageRef}
                      src={
                        outputFiles[currentOutputFileIndex]?.imageUrl ||
                        outputFiles[currentOutputFileIndex]?.originalUrl
                      }
                      alt={`Output file ${currentOutputFileIndex + 1}`}
                      className="max-w-full h-auto block"
                      onLoad={handleImageLoad}
                    />
                    <canvas
                      ref={canvasRef}
                      className="absolute top-0 left-0 w-full h-full pointer-events-none"
                    />
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <ImageIcon className="w-16 h-16 text-slate-400 mx-auto mb-4" />
                    <p className="text-slate-500">No output files found in this project</p>
                  </div>
                )}

                {outputFiles.length > 0 && currentAnnotations.length === 0 && (
                  <div className="mt-4 text-center">
                    <p className="text-sm text-slate-500 bg-slate-50 rounded-lg px-4 py-3 border border-slate-200">
                      No annotations found for this output file
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="xl:col-span-1">
            <div className="bg-white rounded-2xl shadow-xl shadow-slate-200/50 border border-slate-200/60 sticky top-24">
              <div className="p-6 border-b border-slate-200/60">
                <h2 className="text-lg font-semibold text-slate-800 mb-4 flex items-center">
                  <Target className="w-5 h-5 mr-2 text-blue-600" />
                  Current Annotations
                </h2>
                {currentAnnotations.length === 0 ? (
                  <div className="text-center py-8">
                    <div className="w-12 h-12 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-3">
                      <Target className="w-6 h-6 text-slate-400" />
                    </div>
                    <p className="text-slate-500 text-sm">No annotations</p>
                    <p className="text-slate-400 text-xs mt-1">This output file has no saved annotations</p>
                  </div>
                ) : (
                  <div className="space-y-3 max-h-80 overflow-y-auto scrollbar-thin scrollbar-thumb-slate-300 scrollbar-track-slate-100">
                    {currentAnnotations.map((annotation, idx) => (
                      <div
                        key={idx}
                        className="group p-4 rounded-xl border bg-gradient-to-br from-slate-50 to-white border-slate-200/60 hover:border-slate-300/60 transition-all duration-200"
                      >
                        <div className="flex items-center justify-between mb-2">
                          <div className="font-semibold text-sm text-slate-700 flex items-center">
                            <div className="w-3 h-3 rounded-full mr-2 bg-blue-500"></div>
                            {annotation.class || `Region ${idx + 1}`}
                          </div>
                          <div className="text-xs text-slate-400">#{idx + 1}</div>
                        </div>
                        <div className="grid grid-cols-2 gap-2 text-xs text-slate-500">
                          <div className="bg-white rounded-md px-2 py-1 border border-slate-100">
                            <span className="font-medium">X:</span> {Math.round(annotation.x)}
                          </div>
                          <div className="bg-white rounded-md px-2 py-1 border border-slate-100">
                            <span className="font-medium">Y:</span> {Math.round(annotation.y)}
                          </div>
                          {annotation.type === "rectangle" && (
                            <>
                              <div className="bg-white rounded-md px-2 py-1 border border-slate-100">
                                <span className="font-medium">W:</span> {Math.round(annotation.width)}
                              </div>
                              <div className="bg-white rounded-md px-2 py-1 border border-slate-100">
                                <span className="font-medium">H:</span> {Math.round(annotation.height)}
                              </div>
                            </>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div className="p-6">
                <h3 className="text-sm font-semibold text-slate-700 mb-4 flex items-center">
                  <CheckCircle2 className="w-4 h-4 mr-2 text-emerald-600" />
                  Project Statistics
                </h3>
                {outputFiles.length > 0 && (
                  <div className="mt-4 mb-2 p-2 bg-white rounded-lg border border-green-200">
                    <h4 className="text-sm font-semibold text-slate-700 mb-3">
                      Preprocessing / Augmentation
                    </h4>

                    <div className="space-y-2 text-xs text-slate-600">
                      {outputFiles[currentOutputFileIndex]?.augmentationType && (
                        <div className="flex justify-between">
                          <span className="font-medium text-blue-600">
                            {outputFiles[currentOutputFileIndex].augmentationType}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                )}
                <div className="space-y-3">
                  <div className="flex items-center justify-between p-3 bg-gradient-to-r from-yellow-50 to-yellow-50/50 rounded-lg border border-yellow-100">
                    <span className="text-sm font-medium text-slate-600">Output Files</span>
                    <span className="text-sm font-bold text-yellow-600">{outputFiles.length}</span>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-gradient-to-r from-purple-50 to-purple-50/50 rounded-lg border border-purple-100">
                    <span className="text-sm font-medium text-slate-600">Total Annotations</span>
                    <span className="text-sm font-bold text-purple-600">
                      {outputFiles.reduce((total, file) => total + (file.annotations ? file.annotations.length : 0), 0)}
                    </span>
                  </div>
                </div>

                <div className="mt-4 p-4 bg-gradient-to-r from-slate-50 to-slate-50/50 rounded-lg border border-slate-100">
                  <div className="text-xs text-slate-600 space-y-1">
                    <div className="flex justify-between">
                      <span>Project:</span>
                      <span className="font-medium">{selectedProject.projectName}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Version:</span>
                      <span className="font-medium">{selectedProject.versionName}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Created By:</span>
                      <span className="font-medium">{selectedProject.createdBy}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Created On:</span>
                      <span className="font-medium">{formatDate(selectedProject.createdAt || selectedProject.createdOn)}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Viewdatasetversion;